import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Loader2, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { logUsageEvent } from '../lib/logger';

interface AvisarAusenciaModalProps {
    isOpen: boolean;
    onClose: () => void;
}

/**
 * Autoatendimento de ausência de última hora — o vendedor não tem permissão de
 * gravar em ausencias_trabalho diretamente (só gestor/admin, decisão do dono do
 * produto), então isso cria uma SOLICITAÇÃO pendente. O gestor aprova/rejeita em
 * /config/escalas — só aí vira uma ausência de verdade. Sem isso, quem passa mal
 * de manhã não teria canal formal pra avisar, e o SLA acabaria medindo alguém
 * injustamente (a ausência real não estaria refletida na escala a tempo).
 */
const AvisarAusenciaModal: React.FC<AvisarAusenciaModalProps> = ({ isOpen, onClose }) => {
    const { user } = useAuth();
    const { addToast } = useToast();
    const [data, setData] = useState(new Date().toISOString().slice(0, 10));
    const [diaInteiro, setDiaInteiro] = useState(true);
    const [horaInicio, setHoraInicio] = useState('08:00');
    const [horaFim, setHoraFim] = useState('12:00');
    const [motivo, setMotivo] = useState('');
    const [salvando, setSalvando] = useState(false);

    if (!isOpen) return null;

    const enviar = async () => {
        if (!user) return;
        setSalvando(true);
        try {
            const { error } = await supabase.from('solicitacoes_ausencia').insert({
                profile_id: user.id,
                data,
                dia_inteiro: diaInteiro,
                hora_inicio: diaInteiro ? null : horaInicio,
                hora_fim: diaInteiro ? null : horaFim,
                motivo: motivo || null,
            });
            if (error) throw error;
            addToast('Aviso enviado — aguardando aprovação do gestor.', 'success');
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'escalas', acao: 'avisar_ausencia', origem: 'acao' });
            setMotivo('');
            onClose();
        } catch (e: any) {
            addToast('Erro ao enviar aviso: ' + (e.message || 'erro desconhecido'), 'error');
        } finally {
            setSalvando(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
            <div className="w-full max-w-md rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-2xl" onClick={e => e.stopPropagation()}>
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <h3 className="text-base font-bold text-gray-900 dark:text-white">Avisar ausência</h3>
                    <button onClick={onClose} className="p-1.5 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"><X size={18} /></button>
                </div>
                <div className="px-6 py-4 space-y-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        Isso avisa seu gestor, que precisa aprovar antes de valer na escala. Use pra avisos de última hora (ex.: passou mal de manhã).
                    </p>
                    <div>
                        <label className="text-xs font-bold text-gray-600 dark:text-gray-300 block mb-1">Data</label>
                        <input type="date" value={data} onChange={e => setData(e.target.value)}
                            className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100" />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                        <input type="checkbox" checked={diaInteiro} onChange={e => setDiaInteiro(e.target.checked)} className="rounded" />
                        Dia inteiro
                    </label>
                    {!diaInteiro && (
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-bold text-gray-600 dark:text-gray-300 block mb-1">Das</label>
                                <input type="time" value={horaInicio} onChange={e => setHoraInicio(e.target.value)}
                                    className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100" />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-gray-600 dark:text-gray-300 block mb-1">Até</label>
                                <input type="time" value={horaFim} onChange={e => setHoraFim(e.target.value)}
                                    className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100" />
                            </div>
                        </div>
                    )}
                    <div>
                        <label className="text-xs font-bold text-gray-600 dark:text-gray-300 block mb-1">Motivo (opcional)</label>
                        <input value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Ex.: Passei mal de manhã"
                            className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100" />
                    </div>
                </div>
                <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-700">Cancelar</button>
                    <button
                        onClick={enviar}
                        disabled={salvando || !data}
                        className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
                    >
                        {salvando ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Enviar aviso
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default AvisarAusenciaModal;
